import { FC, useEffect, useState, useRef } from 'react';
import { connect } from 'react-redux';
import Axios, { CancelTokenSource, AxiosRequestConfig } from 'axios';
import { useTranslation } from 'react-i18next';

import SearchListSelector from '~src/shared/selectors/searchListSelector/SearchListSelector';
import { OptionItem } from '~src/shared/selectors/DdSearchContent';
import { StoreI } from '~src/rootReducer';
import {
  fetchAttributes,
  FetchAttributesParams,
  fetchSingleAttribute,
} from '~src/modules/attributes/actions/attributes.actions';
import { ApiReduxResp } from '~src/services/apiMiddleware';
import { AttributesRespI, AttrSortingType } from '~src/modules/attributes/interfaces';
import { PaginationI } from '~src/interfaces/pagination.interface';
import { AttributeI, AttrInternalType } from '~src/interfaces/attributes.types';
import { translateContent } from '~src/services/translationServices';

interface StateProps {
  loadingAttributes: boolean;
  attributes: AttributeI[];
  attributesSearch: AttributeI[];
  pagination: PaginationI | null;
  paginationSearch: PaginationI | null;
}

interface DispatchProps {
  fetchAttribute: (attributeId: string) => ApiReduxResp<AttributeI>;
  fetchAttrs: (
    params: FetchAttributesParams,
    loadMore?: boolean,
    isSearch?: boolean,
    config?: AxiosRequestConfig,
  ) => ApiReduxResp<AttributesRespI>;
}

interface OwnProps {
  initialOptId?: string | null;
  onSelect(opt: OptionItem): void;
}

export const defaultQueryParams: FetchAttributesParams = {
  'filter.type': AttrInternalType.Option,
  sort: AttrSortingType.CreatedAsc,
  per_page: 10,
};

const initialOptSlug = 'department';

type Props = OwnProps & StateProps & DispatchProps;

const AttributeSelector: FC<Props> = (props) => {
  const [t] = useTranslation();
  const [optionsList, setOptionsList] = useState<OptionItem[] | null>(null);
  const [initialOpt, setInitialOpt] = useState<OptionItem>();
  const [isSearch, setIsSearch] = useState<boolean>();
  const cancelTokenSource = useRef<CancelTokenSource | null>(null);

  useEffect(() => {
    updateOptsList();
  }, [props.attributes, props.attributesSearch]);

  const fetchAttrs = (params?: FetchAttributesParams | string | null, loadMore?: boolean): Promise<any> => {
    let mergedParams: FetchAttributesParams = {};
    let isSearchProp = false;

    if (typeof params === 'string' && !!params) {
      mergedParams = { 'filter.name': params };
      isSearchProp = true;
    } else if (!!params && typeof params === 'object') {
      mergedParams = params;
    }

    if (loadMore) {
      mergedParams.page = props[isSearchProp ? 'paginationSearch' : 'pagination']!.next_page;
    }

    if (cancelTokenSource.current) {
      cancelTokenSource.current.cancel();
    }
    cancelTokenSource.current = Axios.CancelToken.source();
    if (isSearch && !isSearchProp) {
      setIsSearch(isSearchProp);
      updateOptsList(true);
      return Promise.resolve();
    } else {
      setIsSearch(isSearchProp);
      return props.fetchAttrs({ ...defaultQueryParams, ...mergedParams }, loadMore, isSearchProp, {
        cancelToken: cancelTokenSource.current.token,
      });
    }
  };

  const updateOptsList = (entireList?: boolean) => {
    let newInitialOpt: OptionItem | undefined;
    const options = props[isSearch && !entireList ? 'attributesSearch' : 'attributes'].map((item) => {
      const mappedOpt: OptionItem = {
        id: item.uuid,
        name: translateContent(item.name),
      };
      // if no option is selected
      if (!initialOpt) {
        // get the initial option by slug...
        const isInitialSlug = !props.initialOptId && item.slug === initialOptSlug;
        // ... or by the id of the url
        const isInitialId = props.initialOptId && item.uuid === props.initialOptId;
        if (isInitialSlug || isInitialId) {
          newInitialOpt = mappedOpt;
        }
      }
      return mappedOpt;
    });
    if (!initialOpt) {
      // if an initial option was found
      if (newInitialOpt) {
        setInitialOpt(newInitialOpt);
        if (!props.initialOptId) {
          props.onSelect(newInitialOpt);
        }
      } else if (props.initialOptId) {
        props.fetchAttribute(props.initialOptId).then((res) => {
          const attribute = res.response ? res.response.data : null;
          if (attribute) {
            setInitialOpt({ id: attribute.uuid, name: translateContent(attribute.name) });
          }
        });
      } else {
        setInitialOpt(options[0]);
        props.onSelect(options[0]);
      }
    }
    setOptionsList(options);
  };

  const isNoMoreItems = () => {
    const pagination = props[isSearch ? 'paginationSearch' : 'pagination'];
    return !!(pagination && !pagination.next_page);
  };

  return (
    <SearchListSelector
      initialOpt={initialOpt}
      placeholder={{
        key: 'x.compare.header.filters.select_attribute.search_placeholder',
        value: t('x.compare.header.filters.select_attribute.search_placeholder'),
      }}
      onSelect={props.onSelect}
      optionsList={optionsList}
      loadingSearch={props.loadingAttributes}
      fetchItems={fetchAttrs}
      nothingFoundKey="x.compare.header.filters.select_attribute.nothing_found"
      noMoreItems={isNoMoreItems()}
    />
  );
};

const mapState2Props = ({ attributes }: StoreI): StateProps => ({
  loadingAttributes: attributes.loading,
  attributes: attributes.items,
  attributesSearch: attributes.itemsSearch,
  pagination: attributes.pagination,
  paginationSearch: attributes.paginationSearch,
});

const mapDispatch2Props = (dispatch: any): DispatchProps => ({
  fetchAttrs: (params, loadMore, isSearch, config) =>
    dispatch(fetchAttributes(params, loadMore, isSearch, config)),
  fetchAttribute: (attributeId) => dispatch(fetchSingleAttribute(attributeId)),
});

const AttributeSelectorC = connect<StateProps, DispatchProps, OwnProps, StoreI>(
  mapState2Props,
  mapDispatch2Props,
)(AttributeSelector);

export default AttributeSelectorC;
